import { useState } from 'react';
import { PlanEntitlement } from '../services/api';
import { PlanEntitlements } from './PlanEntitlements';
import { formatCents } from '../services/format';

/**
 * Cost-disclosure confirmation for a single-tenant plan change.
 *
 * The plan dropdown on TenantDetailPage no longer mutates directly — it
 * opens this modal first so the operator explicitly sees the cost-driving
 * deltas (block storage, billable IONOS LoadBalancers, isolation mode /
 * dedicated node pool, reserved CPU/RAM) BEFORE the change is dispatched,
 * and must enter a justification (mirrored into the plan history). The
 * cost DRIVERS are always shown; € list prices (base fee, seat price,
 * minimum seats) appear when an active price list exists (`plan.pricing`).
 */

const RANK: Record<string, number> = { basic: 0, business: 1, enterprise: 2 };

interface Props {
  currentPlan: string;
  targetPlan: string;
  plans: PlanEntitlement[];
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

function Delta({
  label,
  from,
  to,
  note,
}: {
  label: string;
  from: string | number;
  to: string | number;
  note?: string;
}) {
  const changed = from !== to;
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="text-right">
        <span className="text-slate-400">{from}</span>
        {' → '}
        <span className={changed ? 'font-semibold text-amber-700' : ''}>{to}</span>
        {note && <span className="block text-xs text-amber-600">{note}</span>}
      </span>
    </div>
  );
}

export function PlanChangeCostModal({ currentPlan, targetPlan, plans, busy, onConfirm, onCancel }: Props) {
  const [reason, setReason] = useState('');

  const cur = plans.find((p) => p.name === currentPlan);
  const tgt = plans.find((p) => p.name === targetPlan);
  const isUpgrade = (RANK[targetPlan] ?? 0) > (RANK[currentPlan] ?? 0);
  const reasonOk = reason.trim().length > 0;

  const lbFrom = cur?.quota.services_loadbalancer ?? 0;
  const lbTo = tgt?.quota.services_loadbalancer ?? 0;
  const isoFrom = cur?.isolation.mode ?? '—';
  const isoTo = tgt?.isolation.mode ?? '—';
  const hasPricing = !!(cur?.pricing || tgt?.pricing);
  const seatLine = (p: PlanEntitlement | undefined) => {
    if (!p?.pricing) return '—';
    const parts = [`${formatCents(p.pricing.seatCents)}/Nutzer${p.pricing.minSeats ? ` (min. ${p.pricing.minSeats})` : ''}`];
    if (p.pricing.seatMonthlyCents !== null) parts.push(`monatlich kündbar ${formatCents(p.pricing.seatMonthlyCents)}`);
    for (const t of p.pricing.volumeTiers ?? []) parts.push(`ab ${t.fromSeats}: ${formatCents(t.seatCents)}`);
    if (p.pricing.hostingSeatCents !== null) {
      parts.push(p.pricing.hostingSeatCents > 0 ? `zzgl. Hosting ${formatCents(p.pricing.hostingSeatCents)}/Nutzer` : 'zzgl. Hosting (Preis offen)');
    }
    return parts.join(' · ');
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">
            Plan-Wechsel: <span className="font-mono">{currentPlan}</span> →{' '}
            <span className="font-mono">{targetPlan}</span>
          </h3>
        </div>

        <div className="p-4 space-y-4">
          {/* Cost / impact disclosure */}
          <div
            className={`rounded p-3 text-sm ${
              isUpgrade
                ? 'bg-amber-50 border border-amber-300 text-amber-900'
                : 'bg-blue-50 border border-blue-300 text-blue-900'
            }`}
          >
            {isUpgrade ? (
              <>
                <p className="font-semibold">⚠️ Dieser Wechsel verursacht zusätzliche laufende Kosten.</p>
                <p className="mt-1">
                  Der höhere Plan provisioniert mehr Infrastruktur — jede dieser Positionen ist
                  kostenpflichtig:
                </p>
              </>
            ) : (
              <>
                <p className="font-semibold">Herabstufung — Ressourcen &amp; Quotas werden reduziert.</p>
                <p className="mt-1">
                  Bestehende Daten/Nutzung oberhalb des Ziel-Quotas können betroffen sein. Bitte vorab
                  prüfen:
                </p>
              </>
            )}
            <div className="mt-2 space-y-0.5">
              {hasPricing && (
                <>
                  <Delta label="Grundgebühr / Monat (Listenpreis)" from={cur?.pricing ? formatCents(cur.pricing.baseCents) : '—'} to={tgt?.pricing ? formatCents(tgt.pricing.baseCents) : '—'} />
                  <Delta label="Preis je Nutzer / Monat (Listenpreis)" from={seatLine(cur)} to={seatLine(tgt)} />
                </>
              )}
              <Delta label="Storage (Block)" from={cur?.quota.storage ?? '—'} to={tgt?.quota.storage ?? '—'} />
              <Delta
                label="LoadBalancer (je ~€/Monat, IONOS NLB)"
                from={lbFrom}
                to={lbTo}
                note={lbTo > lbFrom ? `+${lbTo - lbFrom} kostenpflichtige LoadBalancer` : undefined}
              />
              <Delta
                label="CPU (reserviert)"
                from={cur?.quota.requests_cpu ?? '—'}
                to={tgt?.quota.requests_cpu ?? '—'}
              />
              <Delta
                label="RAM (reserviert)"
                from={cur?.quota.requests_memory ?? '—'}
                to={tgt?.quota.requests_memory ?? '—'}
              />
              <Delta
                label="Isolation"
                from={isoFrom}
                to={isoTo}
                note={
                  isoTo === 'isolated' && isoFrom !== 'isolated'
                    ? 'eigener Node-Pool + vCluster — deutlich höhere Infrastrukturkosten'
                    : undefined
                }
              />
            </div>
          </div>

          {/* Full entitlements side-by-side */}
          {plans.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Aktuell: {currentPlan}</p>
                <PlanEntitlements plan={currentPlan} plans={plans} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Neu: {targetPlan}</p>
                <PlanEntitlements plan={targetPlan} plans={plans} />
              </div>
            </div>
          )}

          {/* Justification — mirrored into the plan history */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Begründung (Pflicht — wird in der Plan-Historie gespeichert)
            </label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Kunde hat Business-Upgrade beauftragt (Angebot #…)"
              className="w-full px-2 py-1 rounded border"
            />
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-3 py-1 rounded border">
            Abbrechen
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !reasonOk}
            className={isUpgrade ? 'btn-danger' : 'btn-primary'}
            title={!reasonOk ? 'Begründung erforderlich' : undefined}
          >
            {busy ? 'Wechsle…' : isUpgrade ? 'Kostenpflichtig wechseln' : 'Plan herabstufen'}
          </button>
        </div>
      </div>
    </div>
  );
}
